import xhr from '../xhr.js';

class UploadSession {
    _appId;
    _fileInput;

    _userAborted = false;
    _abortHandler;
    _progressHandler;

    constructor(appId, fileInput) {
        this._appId = appId;
        this._fileInput = fileInput;
    }

    abort() {
        this._userAborted = true;
        if(typeof this._abortHandler === 'function')
            this._abortHandler();
    }

    onProgress(handler) {
        if(typeof handler !== 'function')
            throw 'handler must be a function';
        this._progressHandler = handler;
    }

    _reportProgress() {
        if(this._progressHandler !== undefined)
            this._progressHandler({
                loaded: ev.loaded,
                total: ev.total,
                progress: ev.total <= 0 ? 0 : ev.loaded / ev.total,
            });
    }

    async start() {
        if(this._userAborted)
            throw 'File upload was cancelled by the user, it cannot be restarted.';

        try {
            const formData = new FormData;
            formData.append('src', this._appId);
            formData.append('file', this._fileInput);

            const { status, body } = await xhr.post('/uploads', {
                type: 'json',
                authed: true,
                upload: this._reportProgress,
                abort: handler => this._abortHandler = handler,
            }, formData);
            if(body === null)
                throw "The upload server didn't return the metadata for some reason.";

            if(status !== 201)
                throw body.english ?? body.error ?? `Upload failed with status code ${status}`;

            body.isImage = () => body.type.startsWith('image/');
            body.isVideo = () => body.type.startsWith('video/');
            body.isAudio = () => body.type.startsWith('audio/');
            body.isMedia = () => body.isImage() || body.isAudio() || body.isVideo();

            return Object.freeze(body);
        } catch(ex) {
            if(this._userAborted)
                throw '';

            console.error(ex);
            throw ex;
        }
    }
}

export default class UploadClient {
    _appId;

    constructor(appId) {
        if(typeof appId !== 'string')
            throw 'appId must be a string';
        this._appId = appId
    }

    createUpload(fileInput) {
        if(!(fileInput instanceof File))
            throw 'fileInput must be an instance of window.File';

        return new UploadSession(this._appId, fileInput);
    }

    async deleteUpload(fileInfo) {
        if(typeof fileInfo !== 'object')
            throw 'fileInfo must be an object';
        if(typeof fileInfo.urlf !== 'string')
            throw 'fileInfo.urlf must be a string';

        const { status, body } = await xhr.delete(fileInfo.urlf, {
            type: 'json',
            authed: true,
        });

        if(status !== 204) {
            if(body === null)
                throw `Delete failed with status code ${status}`;

            throw body.english ?? body.error ?? `Delete failed with status code ${status}`;
        }
    }
};
